import {
  AnnualEmissionsByContext,
  CarbonBudgetOutput,
  EmissionsReductions,
  PathwayDraft,
  PathwayEvaluationConfigured,
  PathwayTrajectory,
  LocalPathway,
} from '@/types';

// Reshaping between the new pathway evaluation wire and the legacy
// scenario-outputs display components — the same charts render the new
// model. Every value is already in the evaluation's scope unit; these
// reshape, never rescale (the server picks the unit, the FE renders it).

// The full-config draft the simulate/save endpoints take, from the
// effective (possibly-unsaved) pathway.
const pathwayDraft = (pathway: LocalPathway): PathwayDraft => ({
  name: pathway.name,
  scenario: pathway.scenario,
  ...(pathway.basePeriod && { basePeriod: pathway.basePeriod }),
  ...(pathway.place !== undefined && { place: pathway.place }),
  interventions: pathway.interventions,
});

// The three trajectories as the annual-emissions chart's shape: series
// from the first projected year; a year with no emissions claim is a
// null (a gap), never a fabricated zero.
const toAnnualEmissions = (
  evaluation: PathwayEvaluationConfigured,
): AnnualEmissionsByContext => ({
  unit: evaluation.unit,
  base: evaluation.current.years[0]?.year ?? 0,
  current: evaluation.current.years.map((y) => y.emissions ?? null),
  intermediate: evaluation.intermediate.years.map((y) => y.emissions ?? null),
  aggressive: evaluation.aggressive.years.map((y) => y.emissions ?? null),
});

// Per-intervention cumulative avoided emissions to the horizon — the
// reductions bar chart's shape. The wire states each intervention's
// avoided emissions per projected year; the chart shows the cumulative
// figure, their sum (same unit, no rescale).
const cumulativeReductions = (trajectory: PathwayTrajectory) =>
  trajectory.reductions.map((reduction) => ({
    name: reduction.name,
    area: reduction.area,
    value: reduction.annual.reduce((acc, v) => acc + v, 0),
  }));

const toReductions = (
  evaluation: PathwayEvaluationConfigured,
): EmissionsReductions => ({
  unit: evaluation.unit,
  current: cumulativeReductions(evaluation.current),
  intermediate: cumulativeReductions(evaluation.intermediate),
  aggressive: cumulativeReductions(evaluation.aggressive),
});

// The per-evaluation remaining budget as the indicators' shape (the
// budget scalar itself rides the component's `total`).
const toBudgetOutput = (
  evaluation: PathwayEvaluationConfigured,
): CarbonBudgetOutput | undefined =>
  evaluation.budget && {
    unit: evaluation.unit,
    current: evaluation.budget.currentRemaining,
    intermediate: evaluation.budget.intermediateRemaining,
    aggressive: evaluation.budget.aggressiveRemaining,
  };

export { pathwayDraft, toAnnualEmissions, toReductions, toBudgetOutput };
